import { useRef, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { TGDComponent, TGDDesign } from '../../../models/courses/graphicDesign';
import { TGDCommandResult, TGDConstraints, updateComponent } from '../core/document';
import { createHistory, pushHistory, redoHistory, replacePresent, undoHistory } from '../core/history';

export const useGDEditor = (initial: TGDDesign, constraints: TGDConstraints) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState(() => createHistory(initial));
  const [selectedId, select] = useState<string | null>(null);
  const [currentColor, setCurrentColor] = useState(constraints.lockedPalette?.[0] ?? '#2d9cc9');
  const [revision, setRevision] = useState(0);
  const previewBase = useRef<TGDDesign | null>(null);
  const warn = (error?: string) => { if (error) message.warning(t(`graphicDesignLesson.errors.${error}`)); };
  const run = (command: (d: TGDDesign) => TGDCommandResult) => {
    previewBase.current = null;
    const result = command(history.present);
    if (result.error) { warn(result.error); return; }
    setHistory(old => pushHistory(old, result.design));
    if (result.design !== history.present) setRevision(value => value + 1);
    if (result.createdId) select(result.createdId);
  };
  const preview = (id: string, patch: Partial<TGDComponent>) => {
    if (!previewBase.current) previewBase.current = history.present;
    setHistory(old => replacePresent(old, updateComponent(old.present, id, patch, constraints).design));
  };
  const commit = (id: string, patch: Partial<TGDComponent>) => {
    const base = previewBase.current ?? history.present;
    previewBase.current = null;
    const result = updateComponent(base, id, patch, constraints);
    if (result.error) { warn(result.error); return; }
    setHistory(old => pushHistory({ ...old, present: base }, result.design));
    if (result.design !== base) setRevision(value => value + 1);
  };
  return {
    design: history.present, selectedId, currentColor, select, setCurrentColor,
    run, preview, commit,
    undo: () => {
      previewBase.current = null;
      setHistory(old => undoHistory(old));
      setRevision(value => value + 1);
    },
    redo: () => {
      previewBase.current = null;
      setHistory(old => redoHistory(old));
      setRevision(value => value + 1);
    },
    canUndo: history.past.length > 0, canRedo: history.future.length > 0,
    revision,
  };
};
